import { WIDGET_CONFIGS } from '@constants';
import type { WidgetConfigElement } from '~/types/widget';
import type { CatalogMap, ControlOption, ControlSchema, ElementSchema } from './types';

const [INCLUDED_BADGE] = WIDGET_CONFIGS.IncludedCart.config as WidgetConfigElement[];

// Shared select option sets reused across element controls.
const ALIGN_ITEMS: ControlOption[] = [
  { label: 'Same height', value: 'stretch' },
  { label: 'Top', value: 'flex-start' },
  { label: 'Middle', value: 'center' },
  { label: 'Bottom', value: 'flex-end' },
  { label: 'Text baseline', value: 'baseline' },
];

/**
 * `align-items` and `justify-content` each describe one axis, and which axis that IS depends on the
 * container's `flex-direction`. Same values throughout — only the words change, so a merchant reads
 * placement in the direction they are actually looking.
 */
const ALIGN_ITEMS_ON_COLUMN: ControlOption[] = [
  { label: 'Full width', value: 'stretch' },
  { label: 'Left', value: 'flex-start' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'flex-end' },
  { label: 'Text baseline', value: 'baseline' },
];

const JUSTIFY_CONTENT_ON_COLUMN: ControlOption[] = [
  { label: 'Top', value: 'flex-start' },
  { label: 'Middle', value: 'center' },
  { label: 'Bottom', value: 'flex-end' },
  { label: 'Spread apart', value: 'space-between' },
  { label: 'Even spacing', value: 'space-around' },
];

const JUSTIFY_CONTENT: ControlOption[] = [
  { label: 'Left', value: 'flex-start' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'flex-end' },
  { label: 'Spread apart', value: 'space-between' },
  { label: 'Even spacing', value: 'space-around' },
];

/**
 * Offered on an element INSIDE a row, writing to the row itself. Where a child sits is stored on
 * its container, but a merchant looks for it on the thing they clicked.
 */
const POSITION_IN_ROW: ControlSchema = {
  attr: 'justify-content',
  group: 'Placement',
  help: 'Where this sits in its row. Turn off "Share the row evenly" first, or the row is already full.',
  kind: 'select',
  label: 'Position in row',
  options: JUSTIFY_CONTENT,
  parentDirection: 'row',
  parentTypes: ['horizontal-stack'],
  target: 'parent',
};

/**
 * The stack counterpart to POSITION_IN_ROW. `vertical-stack` lays out as a column, so `align-items`
 * is its CROSS axis — i.e. horizontal placement — which is why the options here read Left/Center/
 * Right rather than the Top/Middle/Bottom the same attribute means on a row. Offered on the child
 * because "centre this button" is a question about the button, not about the group.
 */
const POSITION_IN_STACK: ControlSchema = {
  attr: 'align-items',
  group: 'Placement',
  help: 'Where this sits across its stack. Give the element a width first, or it fills the stack and has nowhere to move.',
  kind: 'select',
  label: 'Position in stack',
  options: [
    { label: 'Full width', value: 'stretch' },
    { label: 'Left', value: 'flex-start' },
    { label: 'Center', value: 'center' },
    { label: 'Right', value: 'flex-end' },
  ],
  parentDirection: 'column',
  parentTypes: ['vertical-stack', 'horizontal-stack'],
  target: 'parent',
};

/**
 * Outer spacing for the group an element sits in. `vertical-stack` is the only container whose
 * `margin` the runtime reads (horizontal-stack and bordered-container both hardcode it away), so
 * this is the one control that adds distance OUTSIDE a group rather than between its children.
 */
const SPACE_AROUND_GROUP: ControlSchema = {
  attr: 'margin',
  group: 'Spacing',
  help: 'Space outside this element\'s group. Accepts one value, or "top right bottom left".',
  kind: 'spacing',
  label: 'Space around group',
  parentTypes: ['vertical-stack'],
  target: 'parent',
};

/**
 * Gap is stored on the container, but a merchant looking at two buttons is asking about the space
 * between *those* — so it is offered on the child as well as on the group itself.
 */
const GAP_IN_GROUP: ControlSchema = {
  attr: 'gap',
  group: 'Placement',
  help: 'The space between this and the others in its group.',
  kind: 'length',
  label: 'Space between items',
  parentTypes: ['horizontal-stack', 'vertical-stack'],
  target: 'parent',
};

/** Row children default to sharing the row; the row layout depends on it. */
const SHARE_ROW: ControlSchema = {
  attr: 'child-flex',
  group: 'Placement',
  help: 'Every element in the row gets the same width.',
  kind: 'toggle',
  label: 'Share the row evenly',
  offValue: '',
  onValue: '1 1 0',
  parentTypes: ['horizontal-stack'],
  target: 'parent',
};

const FONT_WEIGHT: ControlOption[] = [
  { label: 'Regular', value: '400' },
  { label: 'Medium', value: '500' },
  { label: 'Semibold', value: '600' },
  { label: 'Bold', value: '700' },
];

const TEXT_TRANSFORM: ControlOption[] = [
  { label: 'None', value: 'none' },
  { label: 'Uppercase', value: 'uppercase' },
  { label: 'Capitalize', value: 'capitalize' },
  { label: 'Lowercase', value: 'lowercase' },
];

const WHITE_SPACE: ControlOption[] = [
  { label: 'Wrap', value: 'normal' },
  { label: 'No Wrap', value: 'nowrap' },
];

const BORDER_STYLE: ControlOption[] = [
  { label: 'Solid', value: 'solid' },
  { label: 'Dashed', value: 'dashed' },
  { label: 'Dotted', value: 'dotted' },
  { label: 'None', value: 'none' },
];

/**
 * The cart widget element catalog. Each entry mirrors the `attrs` of the matching
 * cart-widget web component (see the `mirrors` comment) so the inspector controls map
 * to real, supported attributes. Verified at test time against the known element-type set.
 */
/** The three tiles of the coverage selector; the warranty tile is a template shared by every warranty length. */
export const COVERAGE_TILE_TIERS = ['none', 'shipping', 'warranty'] as const;
export type CoverageTileTier = (typeof COVERAGE_TILE_TIERS)[number];

/** Seed copy for each tile — the same wording the cart-widget falls back to when a tile has none. */
export const COVERAGE_TILE_DEFAULTS: Record<
  CoverageTileTier,
  { features: string; 'price-label': string; title: string }
> = {
  none: {
    features: 'No Resolutions for Shipping Issues\nNo Free Returns',
    'price-label': 'Free',
    title: 'No Coverage',
  },
  shipping: {
    features: '24/7 Support\nResolutions for Shipping Issues\n45 Day Returns',
    'price-label': '',
    title: 'Basic',
  },
  warranty: {
    features: 'Everything in Basic Coverage\n{{duration}} Year Extended Warranty',
    'price-label': '',
    title: '{{duration}} Year',
  },
};

export const CART_CATALOG: CatalogMap = {
  // mirrors cart-widget/libs/ui/src/components/border.ts
  'bordered-container': {
    canHaveChildren: true,
    category: 'layout',
    controls: [
      { attr: 'background-color', group: 'Style', kind: 'color', label: 'Background' },
      {
        attr: 'flex-direction',
        group: 'Layout',
        kind: 'select',
        label: 'Direction',
        options: [
          { label: 'Row', value: 'row' },
          { label: 'Column', value: 'column' },
        ],
      },
      {
        attr: 'align-items',
        axis: 'cross',
        group: 'Layout',
        kind: 'select',
        label: 'Vertical Align',
        options: ALIGN_ITEMS,
      },
      { attr: 'gap', group: 'Layout', kind: 'length', label: 'Gap' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
      { attr: 'border-width', group: 'Border', kind: 'length', label: 'Border Width' },
      { attr: 'border-color', group: 'Border', kind: 'color', label: 'Border Color' },
      { attr: 'border-style', group: 'Border', kind: 'select', label: 'Border Style', options: BORDER_STYLE },
      { attr: 'border-radius', group: 'Border', kind: 'length', label: 'Border Radius' },
    ],
    defaultAttributes: {
      'align-items': 'stretch',
      'background-color': 'transparent',
      'border-color': '#E5E7EB',
      'border-radius': '4px',
      'border-style': 'solid',
      'border-width': '1px',
      'flex-direction': 'row',
      gap: '0',
      padding: '8px 16px',
    },
    displayName: 'Bordered Container',
    icon: 'LineBox',
    paletteVisible: true,
    type: 'bordered-container',
  },

  // mirrors cart-widget/libs/ui/src/components/text.ts
  'custom-text': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'content',
    controls: [
      { attr: 'text', group: 'Content', kind: 'textarea', label: 'Text' },
      { attr: 'font', group: 'Content', kind: 'font', label: 'Font' },
      { attr: 'size', group: 'Content', kind: 'length', label: 'Size' },
      { attr: 'color', group: 'Content', kind: 'color', label: 'Color' },
      { attr: 'weight', group: 'Content', kind: 'select', label: 'Thickness', options: FONT_WEIGHT },
      { attr: 'text-transform', group: 'Content', kind: 'select', label: 'Letter Case', options: TEXT_TRANSFORM },
      { attr: 'letter-spacing', group: 'Content', keywords: ['normal'], kind: 'length', label: 'Letter Spacing' },
      {
        attr: 'line-height',
        group: 'Content',
        keywords: ['normal'],
        kind: 'length',
        label: 'Line Height',
        unitless: true,
      },
      {
        attr: 'action',
        group: 'Behavior',
        help: 'What happens when a shopper clicks this text. Leave as None for a plain caption.',
        kind: 'select',
        label: 'Action',
        options: [
          { label: 'None', value: '' },
          { label: 'Add Protection', value: 'add' },
          { label: 'Remove Protection', value: 'remove' },
        ],
      },
      { attr: 'href', group: 'Advanced', kind: 'text', label: 'Link URL' },
    ],
    convertsTo: ['ui-button'],
    defaultAttributes: {
      action: '',
      color: '#1D2433',
      font: 'Inter',
      'letter-spacing': 'normal',
      'line-height': '1em',
      size: '14px',
      text: 'Text',
      'text-transform': 'none',
      weight: '400',
    },
    displayName: 'Text',
    emitsHints: ['protection-remove'],
    icon: 'LineFont',
    paletteVisible: true,
    type: 'custom-text',
  },

  // mirrors cart-widget/libs/ui/src/components/ew-coverage.ts
  'ew-coverage': {
    allowedChildTypes: ['ew-coverage-option'],
    canHaveChildren: true,
    category: 'control',
    controls: [
      {
        attr: 'show-none',
        group: 'Content',
        kind: 'toggle',
        label: 'Offer No Coverage',
        offValue: 'false',
        onValue: 'true',
      },
      {
        attr: 'disclaimer',
        group: 'Details',
        help: 'One short line shown while a warranty tier is selected.',
        kind: 'text',
        label: 'Warranty disclaimer',
      },
      { attr: 'terms-label', group: 'Details', kind: 'text', label: 'Terms link text', placeholder: 'Terms' },
      {
        attr: 'terms',
        group: 'Details',
        help: 'The full wording, revealed when a shopper taps the terms link. Leave empty to hide the link.',
        kind: 'textarea',
        label: 'Warranty terms',
      },
      { attr: 'font', group: 'Style', kind: 'font', label: 'Font' },
      { attr: 'font-size', group: 'Style', kind: 'length', label: 'Font Size' },
      { attr: 'text-color', group: 'Style', kind: 'color', label: 'Text Color' },
      { attr: 'muted-color', group: 'Style', kind: 'color', label: 'Secondary Text Color' },
      { attr: 'accent-color', group: 'Style', kind: 'color', label: 'Selected Color' },
      { attr: 'selected-background', group: 'Style', kind: 'color', label: 'Selected Background' },
      { attr: 'chip-background', group: 'Style', kind: 'color', label: 'Option Background' },
      { attr: 'border-color', group: 'Style', kind: 'color', label: 'Border Color' },
      { attr: 'chip-radius', group: 'Style', kind: 'length', label: 'Corner Radius' },
      { attr: 'chip-gap', group: 'Style', kind: 'length', label: 'Space Between Options' },
      { attr: 'chip-min-height', group: 'Style', kind: 'length', label: 'Option Height' },
    ],
    defaultAttributes: {
      'accent-color': '#5B1FD3',
      'border-color': '#C9D0DD',
      'chip-background': '#FFFFFF',
      'chip-gap': '6px',
      'chip-min-height': '56px',
      'chip-radius': '8px',
      disclaimer: 'Includes shipping protection, priority support & an extended warranty.',
      font: 'inherit',
      'font-size': '13px',
      'muted-color': 'rgba(29, 36, 51, 0.6)',
      'selected-background': '#F3EEFE',
      'show-none': 'true',
      terms:
        'By leaving Extended Coverage selected, I agree to include shipping protection, priority support, and an extended warranty to my order at checkout.',
      'terms-label': 'Terms',
      'text-color': '#1D2433',
    },
    defaultChildren: COVERAGE_TILE_TIERS.map(tier => ({
      attributes: { tier, ...COVERAGE_TILE_DEFAULTS[tier] },
      type: 'ew-coverage-option',
    })),
    displayName: 'Coverage Options',
    emitsHints: ['coverage/tier-selected'],
    icon: 'LineShieldCheck',
    listensHints: ['cart/updated', 'coverage/tier-updated'],
    notice:
      'Click a tile to edit its wording. Warranty tiles appear only for carts within your extended warranty pricing; otherwise shoppers see No Coverage and Basic.',
    paletteVisible: true,
    type: 'ew-coverage',
  },

  // mirrors cart-widget/libs/ui/src/components/ew-coverage-option.ts
  'ew-coverage-option': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'control',
    controls: [
      {
        attr: 'title',
        group: 'Content',
        help: 'On the warranty tile {{duration}} becomes the number of years.',
        kind: 'text',
        label: 'Label',
      },
      {
        attr: 'price-label',
        group: 'Content',
        help: 'Shown in place of the price.',
        kind: 'text',
        label: 'Price text',
        placeholder: 'Free',
        showWhenMatches: { attr: 'tier', pattern: '^none$' },
      },
      {
        attr: 'features',
        group: 'Details',
        help: 'One line per item, shown when a shopper taps (i).',
        kind: 'textarea',
        label: 'Details',
      },
      {
        attr: 'badge-text',
        group: 'Badge',
        help: 'On the warranty tile the badge goes on the longest cover.',
        kind: 'text',
        label: 'Badge',
        placeholder: 'Popular',
      },
    ],
    defaultAttributes: {
      'badge-text': '',
      features: '',
      'price-label': '',
      tier: 'shipping',
      title: '',
    },
    displayName: 'Coverage Tile',
    icon: 'LineShieldCheck',
    locked: true,
    paletteVisible: false,
    type: 'ew-coverage-option',
  },

  // mirrors cart-widget/libs/ui/src/components/horizontal-stack.ts
  'horizontal-stack': {
    canHaveChildren: true,
    category: 'layout',
    controls: [
      { attr: 'gap', group: 'Layout', kind: 'length', label: 'Gap' },
      {
        attr: 'flex-direction',
        group: 'Layout',
        kind: 'select',
        label: 'Direction',
        options: [
          { label: 'Side by side', value: 'row' },
          { label: 'Side by side, reversed', value: 'row-reverse' },
          { label: 'Stacked', value: 'column' },
          { label: 'Stacked, reversed', value: 'column-reverse' },
        ],
      },
      {
        attr: 'child-flex',
        group: 'Layout',
        help: 'Every element in this row gets the same width.',
        kind: 'toggle',
        label: 'Share the row evenly',
        offValue: '',
        onValue: '1 1 0',
      },
      {
        attr: 'align-items',
        axis: 'cross',
        group: 'Layout',
        kind: 'select',
        label: 'Vertical Align',
        options: ALIGN_ITEMS,
      },
      {
        attr: 'justify-content',
        axis: 'main',
        group: 'Layout',
        kind: 'select',
        label: 'Horizontal Align',
        options: JUSTIFY_CONTENT,
      },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width' },
      { attr: 'height', group: 'Layout', kind: 'length', label: 'Height' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
    ],
    defaultAttributes: {
      'align-items': 'center',
      'child-flex': '',
      'flex-direction': 'row',
      gap: '8px',
      height: 'auto',
      'justify-content': 'flex-start',
      padding: '0',
      width: 'auto',
    },
    displayName: 'Side by Side',
    groupBandLabel: 'Side by side',
    icon: 'LineEllipsisH',
    paletteVisible: true,
    type: 'horizontal-stack',
  },

  // mirrors cart-widget/libs/ui/src/components/included-badge.ts
  'included-badge': {
    allowedChildTypes: ['ui-modal'],
    canHaveChildren: true,
    category: 'content',
    controls: [
      { attr: 'text', group: 'Content', kind: 'textarea', label: 'Text' },
      { attr: 'show-icon', group: 'Content', kind: 'toggle', label: 'Show shield icon' },
      { attr: 'font', group: 'Content', kind: 'font', label: 'Font' },
      { attr: 'size', group: 'Content', kind: 'length', label: 'Size' },
      { attr: 'color', group: 'Content', kind: 'color', label: 'Text color' },
      { attr: 'background', group: 'Content', kind: 'color', label: 'Fill' },
    ],
    defaultAttributes: INCLUDED_BADGE.attributes,
    defaultChildren: INCLUDED_BADGE.children,
    displayName: 'Included badge',
    icon: 'LineShieldCheck',
    notice: 'Delete its Info Modal layer to hide the info icon.',
    paletteVisible: true,
    type: 'included-badge',
  },

  'list-group': {
    allowedChildTypes: ['list-item'],
    canHaveChildren: true,
    category: 'layout',
    controls: [
      { attr: 'gap', group: 'Layout', kind: 'length', label: 'Gap' },
      {
        attr: 'alignItems',
        axis: 'cross',
        group: 'Layout',
        kind: 'select',
        label: 'Vertical Align',
        options: ALIGN_ITEMS,
      },
      {
        attr: 'justifyContent',
        axis: 'main',
        group: 'Layout',
        kind: 'select',
        label: 'Horizontal Align',
        options: JUSTIFY_CONTENT,
      },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
      { attr: 'sortByOrder', group: 'Behavior', kind: 'toggle', label: 'Sort By Order' },
    ],
    defaultAttributes: {
      alignItems: 'flex-start',
      gap: '8px',
      justifyContent: 'flex-start',
      padding: '0px',
      sortByOrder: true,
      width: 'auto',
    },
    displayName: 'List Group',
    icon: 'LineListOl',
    paletteVisible: true,
    type: 'list-group',
  },

  // mirrors cart-widget/libs/ui/src/components/list-item.ts
  'list-item': {
    canHaveChildren: true,
    category: 'content',
    controls: [
      { attr: 'gap', group: 'Layout', kind: 'length', label: 'Gap' },
      {
        attr: 'alignItems',
        axis: 'cross',
        group: 'Layout',
        kind: 'select',
        label: 'Vertical Align',
        options: ALIGN_ITEMS,
      },
    ],
    defaultAttributes: { alignItems: 'center', gap: '6px' },
    displayName: 'List Item',
    icon: 'LineListUl',
    paletteVisible: false,
    type: 'list-item',
  },

  // mirrors cart-widget/libs/ui/src/components/collapsible-text.ts
  'op-collapsible': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'content',
    controls: [
      { attr: 'text', group: 'Content', kind: 'textarea', label: 'Text' },
      { attr: 'maxLength', group: 'Content', kind: 'number', label: 'Collapse After (chars)' },
      { attr: 'textColor', group: 'Text', kind: 'color', label: 'Text Color' },
      { attr: 'expandColor', group: 'Text', kind: 'color', label: 'Expand Link Color' },
      { attr: 'size', group: 'Text', kind: 'length', label: 'Size' },
      { attr: 'font', group: 'Text', kind: 'font', label: 'Font' },
    ],
    defaultAttributes: {
      expandColor: '#0066cc',
      font: 'Inter',
      maxLength: 100,
      size: '12px',
      text: '',
      textColor: '#1d2433cc',
    },
    displayName: 'Collapsible Text',
    icon: 'LineAngleDown',
    paletteVisible: true,
    type: 'op-collapsible',
  },

  // mirrors cart-widget/libs/ui/src/components/list-group.ts
  // Projects a control from the theme's own markup (its View Cart button) into the widget layout.
  // Requires the node to be a direct child of <order-protection>; the runtime reports when it is not.
  'op-slot': {
    canHaveChildren: false,
    category: 'layout',
    controls: [
      {
        attr: 'source-selector',
        group: 'Content',
        help: 'CSS selector for the theme control to borrow. It must be a DIRECT child of <order-protection>.',
        kind: 'text',
        label: 'Theme Selector',
        placeholder: 'a.op-view-cart',
      },
      { attr: 'name', group: 'Content', kind: 'text', label: 'Slot Name' },
      { attr: 'flex', group: 'Layout', kind: 'text', label: 'Flex' },
      { attr: 'min-width', group: 'Layout', kind: 'length', label: 'Min Width' },
      // Presentation is optional: leave these empty and the theme's own styling shows through.
      // Set them to match the projected control to the widget's own button.
      { attr: 'background', group: 'Style', kind: 'color', label: 'Background' },
      { attr: 'color', group: 'Style', kind: 'color', label: 'Text Color' },
      { attr: 'border', group: 'Border', kind: 'text', label: 'Border' },
      { attr: 'border-color', group: 'Border', kind: 'color', label: 'Border Color' },
      { attr: 'border-width', group: 'Border', kind: 'length', label: 'Border Width' },
      { attr: 'border-style', group: 'Border', kind: 'select', label: 'Border Style', options: BORDER_STYLE },
      { attr: 'border-radius', group: 'Border', kind: 'length', label: 'Border Radius' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
      { attr: 'font', group: 'Text', kind: 'font', label: 'Font' },
      { attr: 'font-size', group: 'Text', kind: 'length', label: 'Font Size' },
      { attr: 'font-weight', group: 'Text', kind: 'select', label: 'Thickness', options: FONT_WEIGHT },
      { attr: 'text-transform', group: 'Text', kind: 'select', label: 'Letter Case', options: TEXT_TRANSFORM },
      { attr: 'letter-spacing', group: 'Text', keywords: ['normal'], kind: 'length', label: 'Letter Spacing' },
      {
        attr: 'line-height',
        group: 'Text',
        keywords: ['normal'],
        kind: 'length',
        label: 'Line Height',
        unitless: true,
      },
      { attr: 'text-decoration', group: 'Text', kind: 'text', label: 'Text Decoration' },
      { attr: 'white-space', group: 'Text', kind: 'select', label: 'Wrapping', options: WHITE_SPACE },
      { attr: 'min-height', group: 'Layout', kind: 'length', label: 'Min Height' },
    ],
    defaultAttributes: {
      background: '',
      border: '',
      'border-color': '',
      'border-radius': '',
      'border-style': '',
      'border-width': '',
      color: '',
      flex: '1 1 0',
      font: '',
      'font-size': '',
      'font-weight': '',
      'letter-spacing': '',
      'line-height': '',
      'min-height': '',
      'min-width': '0',
      name: 'op-view-cart',
      padding: '',
      'source-selector': '',
      'text-decoration': '',
      'text-transform': '',
      'white-space': '',
    },
    displayName: 'Theme Slot',
    icon: 'LineInbox',
    paletteVisible: true,
    type: 'op-slot',
  },

  // mirrors cart-widget/libs/ui/src/components/radio-group.ts
  'radio-group': {
    allowedChildTypes: ['radio-option'],
    canHaveChildren: true,
    category: 'control',
    controls: [],
    defaultAttributes: { 'selected-option': '' },
    displayName: 'Radio Group',
    icon: 'LineListUl',
    listensHints: ['radio-option/selected', 'coverage/selection-updated'],
    paletteVisible: true,
    type: 'radio-group',
  },

  // mirrors cart-widget/libs/ui/src/components/radio-option.ts
  'radio-option': {
    canHaveChildren: true,
    category: 'control',
    controls: [
      { attr: 'id', group: 'Content', kind: 'text', label: 'Option ID' },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width' },
    ],
    defaultAttributes: { flex: 'none', id: '', selected: false, width: 'auto' },
    displayName: 'Radio Option',
    emitsHints: ['radio-option/selected'],
    icon: 'LineCheckCircle',
    paletteVisible: false,
    type: 'radio-option',
  },

  // mirrors cart-widget/libs/ui/src/components/cart-action-button.ts
  'ui-button': {
    allowedChildTypes: ['custom-text', 'ui-price', 'ui-image'],
    canHaveChildren: true,
    category: 'control',
    controls: [
      { attr: 'text', group: 'Content', kind: 'text', label: 'Label' },
      // Free text rather than a select: `icon` takes a preset name OR an uploaded SVG's URL.
      {
        attr: 'icon',
        group: 'Content',
        help: 'A preset name — lock, shield, truck, box, check — or the address of an uploaded SVG.',
        kind: 'text',
        label: 'Icon',
        placeholder: 'shield',
      },
      { attr: 'icon-color', group: 'Content', kind: 'color', label: 'Icon Color' },
      { attr: 'icon-size', group: 'Content', kind: 'length', label: 'Icon Size' },
      {
        attr: 'action',
        group: 'Behavior',
        help: 'What happens when a shopper clicks this button.',
        kind: 'select',
        label: 'Action',
        options: [
          { label: 'Add Protection', value: 'add' },
          { label: 'Remove Protection', value: 'remove' },
          { label: 'Go to Cart', value: 'cart' },
        ],
      },
      // Only meaningful once the button navigates, and pre-filled with the cart path when it does,
      // so the common case needs no typing and an unusual destination is still reachable.
      {
        attr: 'href',
        group: 'Behavior',
        help: 'Where the button sends a shopper. A path on this store, or a full https address.',
        kind: 'text',
        label: 'Link To',
        placeholder: '/cart',
        showWhenMatches: { attr: 'action', pattern: '^cart$' },
      },
      { attr: 'background-color', group: 'Content', kind: 'color', label: 'Background' },
      { attr: 'color', group: 'Content', kind: 'color', label: 'Text Color' },
      { attr: 'font', group: 'Content', kind: 'font', label: 'Font' },
      { attr: 'font-size', group: 'Content', kind: 'length', label: 'Font Size' },
      { attr: 'text-weight', group: 'Content', kind: 'select', label: 'Thickness', options: FONT_WEIGHT },
      { attr: 'text-transform', group: 'Content', kind: 'select', label: 'Letter Case', options: TEXT_TRANSFORM },
      { attr: 'letter-spacing', group: 'Content', keywords: ['normal'], kind: 'length', label: 'Letter Spacing' },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width', placeholder: 'auto, 100%, 200px' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
      { attr: 'border-radius', group: 'Border', kind: 'length', label: 'Border Radius' },
      { attr: 'border-color', group: 'Border', kind: 'color', label: 'Border Color' },
      { attr: 'border-width', group: 'Border', kind: 'length', label: 'Border Width' },
      { attr: 'border-style', group: 'Border', kind: 'select', label: 'Border Style', options: BORDER_STYLE },
    ],
    convertsTo: ['custom-text'],
    defaultAttributes: {
      action: 'add',
      'background-color': '#000000',
      'border-color': 'transparent',
      'border-radius': '4px',
      'border-style': 'none',
      'border-width': '0px',
      color: '#ffffff',
      font: 'Inter',
      'font-size': '14px',
      href: '',
      icon: '',
      'icon-color': '',
      'icon-size': '1.15em',
      'letter-spacing': '',
      padding: '8px 16px',
      text: 'Add Protection',
      'text-transform': 'none',
      'text-weight': '400',
      width: 'auto',
    },
    displayName: 'Button',
    emitsHints: ['protection-add'],
    icon: 'LineShieldCheck',
    paletteVisible: true,
    type: 'ui-button',
  },

  // mirrors cart-widget/libs/ui/src/components/image.ts
  'ui-image': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'media',
    controls: [
      { attr: 'src', group: 'Content', kind: 'image', label: 'Image URL' },
      { attr: 'alt', group: 'Content', kind: 'text', label: 'Alt Text' },
      // The runtime fetches the file and draws its markup inline, which is what makes the shapes
      // reachable by CSS; a plain <img> cannot be recoloured at all.
      // Offered only for a source that actually is an SVG: the runtime inlines a non-http `src`
      // verbatim as markup, so switching this on for a PNG or a data URI renders its bytes as text.
      {
        attr: 'isSvg',
        group: 'Content',
        help: 'Draws the file inline so its colour can be set.',
        kind: 'toggle',
        label: 'This is an SVG',
        showWhenMatches: { attr: 'src', pattern: '\\.svg([?#]|$)|^data:image/svg\\+xml|^\\s*<svg' },
      },
      {
        attr: 'color',
        group: 'Content',
        help: 'Recolours the artwork, keeping any white cut-out shapes white. Leave empty for the colours in the file.',
        kind: 'color',
        label: 'Logo Color',
        showWhenMatches: { attr: 'src', pattern: '\\.svg([?#]|$)|^data:image/svg\\+xml|^\\s*<svg' },
      },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width', placeholder: 'auto, 40px, 100%' },
      { attr: 'height', group: 'Layout', kind: 'length', label: 'Height' },
      { attr: 'opacity', group: 'Style', kind: 'number', label: 'Opacity' },
      { attr: 'href', group: 'Advanced', kind: 'text', label: 'Link URL' },
    ],
    defaultAttributes: { alt: '', color: '', height: '40px', isSvg: false, opacity: 1, src: '', width: '40px' },
    displayName: 'Image',
    icon: 'LineImage',
    paletteVisible: true,
    type: 'ui-image',
  },

  // mirrors cart-widget/libs/ui/src/components/modal-container.ts
  'ui-modal': {
    canHaveChildren: true,
    category: 'content',
    controls: [
      { attr: 'modalHeading', group: 'Content', kind: 'text', label: 'Heading' },
      { attr: 'modalSubheading', group: 'Content', kind: 'text', label: 'Subheading' },
      { attr: 'modalFooterText', group: 'Content', kind: 'textarea', label: 'Footer Text' },
      { attr: 'modalFileClaimLink', group: 'Advanced', kind: 'text', label: 'File Claim Link' },
      { attr: 'modalShowCopyright', group: 'Content', kind: 'toggle', label: 'Show Copyright' },
    ],
    defaultAttributes: { 'aria-label': 'Information Modal', role: 'dialog' },
    displayName: 'Info Modal',
    icon: 'LineInbox',
    paletteVisible: false,
    type: 'ui-modal',
  },

  // mirrors cart-widget/libs/ui/src/components/price.ts
  'ui-price': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'content',
    controls: [
      { attr: 'font', group: 'Content', kind: 'font', label: 'Font' },
      { attr: 'size', group: 'Content', kind: 'length', label: 'Size' },
      { attr: 'color', group: 'Content', kind: 'color', label: 'Color' },
      { attr: 'weight', group: 'Content', kind: 'select', label: 'Thickness', options: FONT_WEIGHT },
      {
        attr: 'type',
        group: 'Behavior',
        kind: 'select',
        label: 'Price Source',
        options: [
          { label: 'Variant Price', value: 'variant' },
          { label: 'Subtotal', value: 'subtotal' },
        ],
      },
      { attr: 'currency', group: 'Behavior', kind: 'text', label: 'Currency' },
    ],
    defaultAttributes: {
      color: '#1D2433',
      currency: 'USD',
      font: 'Inter',
      size: '14px',
      type: 'variant',
      weight: '600',
    },
    displayName: 'Price',
    icon: 'LineUsdCircle',
    listensHints: ['widget/loaded', 'cart/updated'],
    paletteVisible: true,
    type: 'ui-price',
  },

  // mirrors cart-widget/libs/ui/src/components/switch.ts
  'ui-switch': {
    allowedChildTypes: [],
    canHaveChildren: false,
    category: 'control',
    controls: [
      {
        attr: 'type',
        group: 'Behavior',
        kind: 'select',
        label: 'Style',
        options: [
          { label: 'Toggle', value: 'toggle' },
          { label: 'Checkbox', value: 'checkbox' },
        ],
      },
      { attr: 'oncolor', group: 'Style', kind: 'color', label: 'On Color' },
      { attr: 'offcolor', group: 'Style', kind: 'color', label: 'Off Color' },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width' },
      { attr: 'height', group: 'Layout', kind: 'length', label: 'Height' },
    ],
    defaultAttributes: {
      height: '20px',
      offcolor: 'rgba(29, 36, 51, 0.35)',
      oncolor: '#23272f',
      type: 'toggle',
      width: '33px',
    },
    displayName: 'Toggle Switch',
    emitsHints: ['protection/toggled'],
    icon: 'LineToggleOn',
    listensHints: ['settings/loaded', 'cart/updated'],
    paletteVisible: true,
    type: 'ui-switch',
  },

  // mirrors cart-widget/libs/ui/src/components/vertical-stack.ts
  'vertical-stack': {
    canHaveChildren: true,
    category: 'layout',
    controls: [
      { attr: 'gap', group: 'Layout', kind: 'length', label: 'Gap' },
      {
        attr: 'align-items',
        axis: 'cross',
        group: 'Layout',
        kind: 'select',
        label: 'Vertical Align',
        options: ALIGN_ITEMS,
      },
      { attr: 'width', group: 'Layout', kind: 'text', label: 'Width' },
      { attr: 'padding', group: 'Spacing', kind: 'spacing', label: 'Padding' },
      { attr: 'margin', group: 'Spacing', kind: 'spacing', label: 'Margin' },
    ],
    defaultAttributes: { 'align-items': 'stretch', gap: '8px', margin: '0', padding: '0', width: 'auto' },
    displayName: 'Stacked Group',
    groupBandLabel: 'Stacked',
    icon: 'LineBars',
    paletteVisible: true,
    type: 'vertical-stack',
  },
};

/**
 * Every element can sit inside a row, so every element offers the two row controls. They write to
 * the containing row rather than the element, and the inspector hides them unless the element
 * actually has a row for a parent — so a button in a row shows "Position in row" while the same
 * button at the top level does not.
 */
/**
 * A container's effective `flex-direction`. `vertical-stack` is a column no matter what it stores —
 * the runtime hardcodes it — while `horizontal-stack` honours the attribute and defaults to a row.
 */
export function effectiveDirection(
  container?: { type: string; attributes?: Record<string, unknown> } | null,
): 'row' | 'column' {
  if (!container) return 'row';
  if (container.type === 'vertical-stack') return 'column';

  const declared = String(container.attributes?.['flex-direction'] ?? 'row');

  return declared.startsWith('column') ? 'column' : 'row';
}

/**
 * How a group describes the way it stacks its children. Derived, not read from the catalog: a
 * `horizontal-stack` switched to a column groups them vertically, and a band still reading "Side by
 * side" then contradicts the Properties panel right next to it.
 */
export function resolveGroupBandLabel(
  bandLabel: string | undefined,
  container?: { type: string; attributes?: Record<string, unknown> } | null,
): string | undefined {
  if (!bandLabel) return bandLabel;

  return effectiveDirection(container) === 'column' ? 'Stacked' : 'Side by side';
}

/**
 * Rewrites an axis control's label and options for the direction its container actually lays out in.
 * Returns the control untouched when it declares no axis, so callers can map every control through.
 */
export function resolveAxisControl(
  control: ControlSchema,
  container?: { type: string; attributes?: Record<string, unknown> } | null,
): ControlSchema {
  if (!control.axis) return control;

  const column = effectiveDirection(container) === 'column';
  // The cross axis is the one the container does NOT lay out along, so it reads horizontal on a
  // column and vertical on a row; the main axis is the other way round.
  const horizontal = control.axis === 'cross' ? column : !column;
  const isAlignItems = control.axis === 'cross';

  const options = horizontal
    ? isAlignItems
      ? ALIGN_ITEMS_ON_COLUMN
      : JUSTIFY_CONTENT
    : isAlignItems
      ? ALIGN_ITEMS
      : JUSTIFY_CONTENT_ON_COLUMN;

  return { ...control, label: horizontal ? 'Horizontal align' : 'Vertical align', options };
}

for (const entry of Object.values(CART_CATALOG)) {
  entry.controls = [GAP_IN_GROUP, SHARE_ROW, POSITION_IN_ROW, POSITION_IN_STACK, SPACE_AROUND_GROUP, ...entry.controls];
}

/** Known cart element type names (used for catalog drift validation in tests). */
export const CART_ELEMENT_TYPES: string[] = Object.keys(CART_CATALOG);

/** Opt-in elements; the storefront ignores a published Included config that contains any of them. */
export const INCLUDED_EXCLUDED_TYPES = ['ui-switch', 'ui-price', 'radio-group', 'radio-option', 'ew-coverage'];

/** The catalog an Included widget can add from. */
export const withoutOptInElements = (catalog: CatalogMap): CatalogMap =>
  Object.fromEntries(Object.entries(catalog).filter(([type]) => !INCLUDED_EXCLUDED_TYPES.includes(type)));

/** Element schemas that should appear in the palette's add list, grouped by category. */
export const CART_PALETTE_ENTRIES: ElementSchema[] = Object.values(CART_CATALOG).filter(e => e.paletteVisible);
